import i18n from "@/i18n";
import globalMethods from "@/globalMethods";
import {URLS} from "@/constants";
import {notify} from "@/plugins/notification";


export default {
    namespaced: true,
    state: {
        clients: [],
        currentClientHistory: [],
        clientsPagination: {
            page: 1,
            pageSize: 50,
            hasNext: false,
            totalCount: 0,
            sortBy: null,
            sortDirection: "asc",
            search: "",
            isLoading: false,
        },
    },
    mutations: {
        setClients(state, data) {
            state.clients = data;
        },
        setCurrentClientHistory(state, clientHistory) {
            state.currentClientHistory = clientHistory;
        },
        addClient(state, clientData) {
            state.clients.unshift(clientData);
            state.clientsPagination.totalCount++;
        },
        updateClient(state, newClientData) {
            const client = state.clients.find(element => element.id === newClientData.id);
            if (!client) return;
            client.email = newClientData.email;
            client.phone = newClientData.phone;
            client.name = newClientData.name;
            client.note = newClientData.note;
            client.discount = newClientData.discount;
            client.use_discount_policy = newClientData.use_discount_policy;
            client.total_payments = newClientData.total_payments;
            client.is_active = newClientData.is_active;
        },
        activateClient(state, clientId) {
            const client = state.clients.find(client => client.id === clientId);
            if (client) client.is_active = true;
        },
        activateClients(state, clientIds) {
            for (const clientId of clientIds) {
                const client = state.clients.find(client => client.id === clientId);
                if (client) client.is_active = true;
            }
        },
        archiveClient(state, clientId) {
            const client = state.clients.find(client => client.id === clientId);
            if (client) client.is_active = false;
        },
        archiveClients(state, clientIds) {
            for (const clientId of clientIds) {
                const client = state.clients.find(client => client.id === clientId);
                if (client) client.is_active = false;
            }
        },
        deleteClient(state, clientId) {
            state.clients = state.clients.filter(client => client.id !== clientId);
            state.clientsPagination.totalCount = Math.max(0, state.clientsPagination.totalCount - 1);
        },
        deleteClients(state, clientIds) {
            for (const clientId of clientIds) {
                state.clients = state.clients.filter(client => client.id !== clientId);
            }
            state.clientsPagination.totalCount = Math.max(0, state.clientsPagination.totalCount - clientIds.length);
        },
        setClientsPaginated(state, { data, hasNext, totalCount, append }) {
            if (append) {
                state.clients = [...state.clients, ...data];
            } else {
                state.clients = data;
            }
            state.clientsPagination.hasNext = hasNext;
            state.clientsPagination.totalCount = totalCount;
        },
        setClientsPaginationLoading(state, isLoading) {
            state.clientsPagination.isLoading = isLoading;
        },
        setClientsPaginationParams(state, params) {
            if (params.page !== undefined) state.clientsPagination.page = params.page;
            if (params.sortBy !== undefined) state.clientsPagination.sortBy = params.sortBy;
            if (params.sortDirection !== undefined) state.clientsPagination.sortDirection = params.sortDirection;
            if (params.search !== undefined) state.clientsPagination.search = params.search;
        },
        resetClientsPagination(state) {
            state.clientsPagination.page = 1;
            state.clientsPagination.hasNext = false;
            state.clientsPagination.totalCount = 0;
        },
        increaseClientTotalPayment(state, newTransactionData) {
            if (newTransactionData?.client?.id) {
                const client = state.clients.find(client => client.id === newTransactionData.client.id);
                if (!client) return;
                client.total_payments[newTransactionData.currency] += Number(newTransactionData.total);
                client.discount = Number(newTransactionData.client.discount);
            }
        },
        decreaseClientTotalPayment(state, transactionData) {
            if (transactionData.client && transactionData.client.id) {
                const client = state.clients.find(client => client.id === transactionData.client.id);
                if (!client) return;
                client.total_payments[transactionData.currency] -= Number(transactionData.total);
                client.discount = Number(transactionData.client.discount);
            }
        },
    },
    actions: {
        async getClientHistory({commit, rootState}, clientId) {
            const {personalData, company} = rootState;
            const url = URLS.MODEL.CLIENT.CLIENT_HISTORY_URL.replace("<ID>", clientId);
            const options = {
                method: "GET",
                headers: {
                    role: personalData.role,
                    companyId: company.companyData?.token,
                },
                credentials: "include",
            };
            const successfulHandler = (data) => {
                commit("setCurrentClientHistory", data.data);
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options, successfulHandler: successfulHandler});
        },
        async setClientsData({commit}, data) {
            commit("setClients", data);
        },
        async getClients({commit, state, rootState}, { searchValue = "", append = false } = {}) {
            const {personalData, company, cashRegister} = rootState;
            const pagination = state.clientsPagination;
            const params = new URLSearchParams();
            const search = searchValue || pagination.search;
            if (search) params.set("search", search);
            if (pagination.page >= 1) {
                params.set("page", pagination.page);
                params.set("page_size", pagination.pageSize);
            }
            if (pagination.sortBy) {
                params.set("sort_by", pagination.sortBy);
                params.set("sort_direction", pagination.sortDirection);
            }
            const queryString = params.toString();
            const url = queryString ? `${URLS.MODEL.CLIENT.CLIENTS_URL}?${queryString}` : URLS.MODEL.CLIENT.CLIENTS_URL;
            const options = {
                method: "GET",
                headers: {
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    shopId: globalMethods.getDataFromLocalStorage("currentCashRegisterId"),
                    timezone: cashRegister.cashRegisterData?.timezone,
                },
                credentials: 'include',
            };
            if (append) commit("setClientsPaginationLoading", true);
            const successfulHandler = (data) => {
                commit("setClientsPaginated", {
                    data: data.data,
                    hasNext: data.has_next ?? false,
                    totalCount: data.total_count ?? data.data.length,
                    append,
                });
                if (append) commit("setClientsPaginationLoading", false);
                return {successfully: true};
            };
            const errorHandler = (data) => {
                if (append) commit("setClientsPaginationLoading", false);
                return {successfully: false, detail: data};
            };
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler, errorHandler});
        },
        async loadNextClientsPage({commit, state, dispatch}) {
            if (!state.clientsPagination.hasNext || state.clientsPagination.isLoading) return;
            commit("setClientsPaginationParams", { page: state.clientsPagination.page + 1 });
            return dispatch("getClients", { append: true });
        },
        async changeClientsSort({commit, dispatch}, { sortBy, sortDirection }) {
            commit("resetClientsPagination");
            commit("setClientsPaginationParams", { page: 1, sortBy, sortDirection });
            return dispatch("getClients");
        },
        async searchClients({commit, dispatch}, search) {
            commit("resetClientsPagination");
            commit("setClientsPaginationParams", { page: 1, search });
            return dispatch("getClients");
        },
        async createClient({commit, rootState}, params) {
            const {personalData, company, cashRegister} = rootState;
            const {totalPayments, name, phone, email, birthday, discount, addToGeneralDiscountPolicy, note, addToList} = params;
            const url = URLS.MODEL.CLIENT.CLIENTS_URL;
            const options = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    shopId: globalMethods.getDataFromLocalStorage("currentCashRegisterId"),
                    timezone: cashRegister.cashRegisterData?.timezone,
                },
                body: JSON.stringify({
                    email: email,
                    phone: phone,
                    name: name,
                    birthday: birthday || null,
                    note: note,
                    discount: discount,
                    use_discount_policy: addToGeneralDiscountPolicy,
                    total_payments: totalPayments.reduce((acc, item) => {acc[item.currency] = item.value; return acc;}, {}),
                }),
                credentials: 'include',
            };
            const successfulHandler = (data) => {
                if (addToList) commit("addClient", data.data);
                return {successfully: true, clientData: data.data};
            };
            const errorHandler = (data) => {
                return {successfully: false, detail: data};
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options, successfulHandler: successfulHandler, errorHandler: errorHandler});
        },
        async updateClient({commit, rootState}, params) {
            const {personalData, company, cashRegister} = rootState;
            const {clientId, email, phone, name, birthday, note, discount, use_discount_policy, total_payments} = params;
            const url = URLS.MODEL.CLIENT.CLIENT_URL.replace("<ID>", clientId);
            const options = {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    shopId: globalMethods.getDataFromLocalStorage("currentCashRegisterId"),
                    timezone: cashRegister.cashRegisterData?.timezone,
                },
                body: JSON.stringify({
                    email: email,
                    phone: phone,
                    name: name,
                    birthday: birthday || null,
                    note: note,
                    discount: discount,
                    use_discount_policy: use_discount_policy,
                    total_payments: total_payments,
                }),
                credentials: 'include',
            };
            const successfulHandler = (data) => {
                commit("updateClient", data.data);
                notify(i18n.global.t("The client has been successfully updated"));
                return {successfully: true, detail: null};
            };
            const errorHandler = (data) => {
                return {successfully: false, detail: data};
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options, successfulHandler: successfulHandler, errorHandler: errorHandler});
        },
        async showClient({commit, rootState}, clientId) {
            const {personalData, company, cashRegister} = rootState;
            const url = URLS.MODEL.CLIENT.CLIENT_URL.replace("<ID>", clientId);
            const options = {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    timezone: cashRegister.cashRegisterData?.timezone,
                },
                body: JSON.stringify({
                    command: "activate",
                }),
                credentials: 'include',
            };
            const successfulHandler = () => {
                commit("activateClient", clientId);
                notify(i18n.global.t("The client has been successfully removed from hidden"));
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options, successfulHandler: successfulHandler});
        },
        async showClients({commit, rootState}, clientIds) {
            const {personalData, company, cashRegister} = rootState;
            const url = URLS.MODEL.CLIENT.CLIENTS_URL;
            const options = {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    timezone: cashRegister.cashRegisterData?.timezone,
                },
                body: JSON.stringify({
                    command: "activate",
                    client_ids: clientIds,
                }),
                credentials: 'include',
            };
            const successfulHandler = () => {
                commit("activateClients", clientIds);
                notify(i18n.global.t("The selected clients have been successfully removed from hidden"));
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options, successfulHandler: successfulHandler});
        },
        async hideClient({commit, rootState}, clientId) {
            const {personalData, company, cashRegister} = rootState;
            const url = URLS.MODEL.CLIENT.CLIENT_URL.replace("<ID>", clientId);
            const options = {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    timezone: cashRegister.cashRegisterData?.timezone,
                },
                body: JSON.stringify({
                    command: "archive",
                }),
                credentials: 'include',
            };
            const successfulHandler = () => {
                commit("archiveClient", clientId);
                notify(i18n.global.t("The client has been successfully added to hidden"));
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options, successfulHandler: successfulHandler});
        },
        async hideClients({commit, rootState}, clientIds) {
            const {personalData, company, cashRegister} = rootState;
            const url = URLS.MODEL.CLIENT.CLIENTS_URL;
            const options = {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    timezone: cashRegister.cashRegisterData?.timezone,
                },
                body: JSON.stringify({
                    command: "archive",
                    client_ids: clientIds,
                }),
                credentials: 'include',
            };
            const successfulHandler = () => {
                commit("archiveClients", clientIds);
                notify(i18n.global.t("The selected clients have been successfully added to hidden"));
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options, successfulHandler: successfulHandler});
        },
        async deleteClient({commit, rootState}, clientId) {
            const {personalData, company, cashRegister} = rootState;
            const url = URLS.MODEL.CLIENT.CLIENT_URL.replace("<ID>", clientId);
            const options = {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    timezone: cashRegister.cashRegisterData?.timezone,
                },
                credentials: 'include',
            };
            const successfulHandler = () => {
                commit("deleteClient", clientId);
                notify(i18n.global.t("The client has been successfully deleted"));
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options, successfulHandler: successfulHandler});
        },
        async deleteClients({commit, rootState}, clientIds) {
            const {personalData, company, cashRegister} = rootState;
            const url = URLS.MODEL.CLIENT.CLIENTS_URL;
            const options = {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                    role: personalData.role,
                    companyId: company.companyData?.token,
                    timezone: cashRegister.cashRegisterData?.timezone,
                },
                body: JSON.stringify({
                    client_ids: clientIds
                }),
                credentials: 'include',
            };
            const successfulHandler = () => {
                commit("deleteClients", clientIds);
                notify(i18n.global.t("The selected clients have been successfully deleted"));
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url: url, options: options, successfulHandler: successfulHandler});
        },
        async sendMessageToClients({rootState}, {clientIds, message}) {
            const {personalData, company} = rootState;
            const url = URLS.MODEL.CLIENT.SEND_MESSAGE_URL;
            const options = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                    role: personalData.role,
                    companyId: company.companyData?.token,
                },
                body: JSON.stringify({client_ids: clientIds, message}),
                credentials: 'include',
            };
            const successfulHandler = (data) => {
                const sent = data.data?.sent_count || 0;
                const total = data.data?.total || 0;
                notify(i18n.global.t("Message sent") + `: ${sent}/${total}`);
                return {successfully: true, sent_count: sent, total};
            };
            const errorHandler = (data) => {
                if (data?.detail === "daily_limit_reached") {
                    notify(i18n.global.t("Daily notification limit reached"), "red", 5000);
                } else {
                    notify(i18n.global.t("Failed to send message"), "red", 4000);
                }
                return {successfully: false};
            };
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler, errorHandler});
        },
        async getNotificationLogs({rootState}, params = {}) {
            const {personalData, company} = rootState;
            const clientId = params?.clientId;
            const page = params?.page || 1;
            const queryParams = [];
            if (clientId) queryParams.push(`client_id=${clientId}`);
            if (page > 1) queryParams.push(`page=${page}`);
            const url = queryParams.length ? `${URLS.MODEL.CLIENT.NOTIFICATION_LOGS_URL}?${queryParams.join("&")}` : URLS.MODEL.CLIENT.NOTIFICATION_LOGS_URL;
            const options = {
                method: "GET",
                headers: {
                    role: personalData.role,
                    companyId: company.companyData?.token,
                },
                credentials: 'include',
            };
            const successfulHandler = (data) => {
                return {successfully: true, data: data.data, has_next: data.has_next, total_count: data.total_count};
            };
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler});
        },
    }
};
